import { Injectable, computed, inject, signal } from '@angular/core';
import {
  declareAllObservables,
  feedsAnotherStream,
  observableVarName,
  subVarName,
  subscribeStatement,
  unsubscribeStatement,
} from '@render/rx-codegen';
import type { EntityId } from '@sim/core/types';
import { SimEngineService } from './sim-engine.service';

/** Why a subscription ended, latched between the tick that raised it and the teardown it explains. */
interface TeardownReason {
  message: string;
  report: string | null;
}

@Injectable({ providedIn: 'root' })
export class RxLogService {
  private readonly engine = inject(SimEngineService);
  private readonly declarationsSignal = signal('');
  private readonly activitySignal = signal<string[]>([]);
  private readonly everSubscribed = new Set<EntityId>();
  private erroredWith: TeardownReason | null = null;

  readonly code = computed(() => {
    const activity = this.activitySignal();
    const declarations = this.declarationsSignal();
    return activity.length === 0 ? declarations : `${declarations}\n\n${activity.join('\n')}`;
  });

  constructor() {
    this.reset();
    this.engine.layoutChanged.subscribe(() => this.reset());
    this.latchTeardownReason();
    this.engine.subscriptionChanged.subscribe(({ subscribed }) => this.logToggle(subscribed));
  }

  /**
   * Subscribes ahead of `subscriptionChanged`, which the same tick fires immediately afterwards —
   * the reason has to be latched before the teardown it explains is logged.
   */
  private latchTeardownReason(): void {
    this.engine.events.subscribe((events) => {
      const errored = events.find((event) => event.type === 'subscriptionErrored');
      if (errored) this.erroredWith = { message: errored.message, report: errored.report };
    });
  }

  private reset(): void {
    this.declarationsSignal.set(declareAllObservables(this.engine.state()));
    this.activitySignal.set([]);
    this.everSubscribed.clear();
    this.erroredWith = null;
  }

  /**
   * One shared `.subscribe()`/`.unsubscribe()` call fans out to every observable currently on the
   * board — except one folded into a join, which `zip()` subscribes to internally.
   */
  private logToggle(subscribed: boolean): void {
    const teardownReason = this.takeTeardownReason();
    if (!subscribed && teardownReason !== null) {
      this.logError(teardownReason);
      return;
    }

    const state = this.engine.state();
    const lines = Object.values(state.observables)
      .filter((observable) => !feedsAnotherStream(state, observable))
      .map((observable) => {
        if (subscribed) {
          const line = subscribeStatement(
            state,
            observable,
            observableVarName(state, observable),
            subVarName(state, observable),
            !this.everSubscribed.has(observable.id),
          );
          this.everSubscribed.add(observable.id);
          return line;
        }
        return unsubscribeStatement(subVarName(state, observable));
      });

    this.activitySignal.update((prev) => [...prev, ...lines]);
  }

  private takeTeardownReason(): TeardownReason | null {
    const reason = this.erroredWith;
    this.erroredWith = null;
    return reason;
  }

  /**
   * A subscription that ended because something threw never had `unsubscribe()` called on it, and
   * logging one would teach the player the opposite of what happened — that they, rather than a
   * terminal error, ended the stream.
   *
   * An unreported failure logs as *uncaught*, which `ts-highlight` renders apart from an ordinary
   * comment: with nothing in the `error` column this line is the only account of the teardown the
   * player gets, so it has to read as the fault it is rather than as commentary.
   */
  private logError(failure: TeardownReason): void {
    const lines =
      failure.report === null
        ? [
            `// Uncaught Error: ${failure.message}`,
            '// an error is terminal: RxJS tore the subscription down, and nothing flows after it',
          ]
        : [
            `// error: ${failure.report}`,
            '// AuditService.report(err) told you why — an error is terminal either way',
          ];
    this.activitySignal.update((prev) => [...prev, ...lines]);
  }
}
